<template>
    <div class="h-full w-full border-gray-200 bg-gray-100 relative">
        <div class="p-1.5 px-3 bg-white flex flex-col justify-center items-center z-20 border-b-2 border-r-2 border-gray-200 fixed"
        :class="width"
        >
            <div class="w-full flex justify-between items-center">
                <CustomTabBar
                    class="flex-1 justify-between space-x-3"
                    :tabList="filterTabs"
                    @set-tab="setFilterTab"
                />
                <button
                    @click="saveDefaultFilters"
                    :disabled="isSavingDefaults"
                    class="ml-2 px-2 py-0.5 text-xs font-medium text-white bg-accent-primary rounded hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title="Save current filter values and pinned state as your defaults"
                >
                    <span class="relative flex items-center justify-center">
                        <InfiniteSpinnerSVG v-if="isSavingDefaults" class="absolute" icon-class="w-8 h-3" stroke-width="20" />
                        <span :class="{ invisible: isSavingDefaults }">Save Defaults</span>
                    </span>
                </button>
            </div>
            <QuickSearch
                class="pt-1 w-full  border-t"
                placeholder="Search filters"
                @quick-search="quickSearch"
                @clear-quick-search="clearQuickSearch"
            />
        </div>
        <div class="h-[calc(100%-7.5rem)] px-1.5 py-3 overflow-y-scroll top-[88px] relative" @dragover.prevent="handleDragOver">
            <TransitionGroup
                tag="ul"
                name="filters"
                v-if="Object.keys(visibleFilters).length > 0"
            >
                <li
                    v-for="(filter, key, index) in visibleFilters"
                    :key="key"
                    :data-index="index"
                    class="px-2"
                    @click="setFilterOptions(key, filter.options)"
                >
                    <FilterRow
                        class="draggableRow"
                        :key="key"
                        :active="isActive(filter)"
                        :index="index"
                        :starting-index="0"
                        :ending-index="Object.keys(filters).length - 1"
                    >
                        <template v-slot:label>
                            <div class="pl-1.5 py-1.5">
                                {{ filter.label }}
                            </div>
                            </template>
                        <template v-slot:expression>
                            <div v-if="filter.expression" class="flex items-center space-x-1 text-xs w-full justify-end">
                                <div class="cursor-hover" v-for="(expressionOption, index) in filter.expression.options" :key="index"
                                    @click="setFilterExpression(filter, expressionOption.value)"
                                >
                                    <div class="cursor-hover" :class="{'underline' : expressionOption.value == filter.expression.value}" >
                                        {{ expressionOption.label }}
                                    </div>
   
                                </div>
                            </div>
                        </template>
                        <template v-slot:button>
                            <div class="cursor-pointer z-10 px-1 py-1.5 hover:bg-gray-200 w-full h-full flex justify-center items-center">
                                <PinFillSVG v-if="filter.pinned" @click="togglePinnedFilter(key)" class="w-3.5 h-3.5 cursor-pointer" color="#464866" />  
                                <PinOutlineSVG v-else  @click="togglePinnedFilter(key)" class="w-3.5 h-3.5 cursor-pointer" color="#464866" />  
                            </div>
                        </template>
                        <template v-slot:widget>
                            <div v-if="filter.widget == 'range'" class="grid grid-cols-2 gap-x-0.5 w-full h-full">
                                <input
                                    class="w-full h-full px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500"
                                    v-model="filter.min.value"
                                    :type="filter.min.type"
                                    :placeholder="filter.min.placeholder"
                                />
                                <input
                                    class="w-full h-full px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500"
                                    v-model="filter.max.value"
                                    :type="filter.max.type"
                                    :placeholder="filter.max.placeholder"
                                />
                            </div>
                            <ComboBox v-else-if="filter.widget == 'combo-box' && filter.multiSelect"
                                class="w-full"
                                v-model="filter.value"
                                :options="filter.options"
                                :multiple="filter.multiSelect"
                                :placeholder="filter.placeholder"
                                @update:modelValue="(event) => setFilter(`${key}`, event)"
                            />
                            <ComboBox v-else-if="filter.widget == 'combo-box'"
                                class="w-full"
                                v-model="filter.value"
                                :options="filter.options"
                                :placeholder="filter.placeholder"
                                :multiple="filter.multiSelect"
                            />
                            <ListBox v-else-if="filter.widget == 'list-box' && filter.multiSelect"
                                class="w-full"
                                v-model="filter.value"
                                :options="filter.options"
                                :placeholder="filter.placeholder"
                                :multiple="filter.multiSelect"
                                @update:modelValue="(event) => setFilter(`${key}`, event)"
                            />
                            <ListBox v-else-if="filter.widget == 'list-box'"
                                class="w-full"
                                v-model="filter.value"
                                :options="filter.options"
                                :multiple="filter.multiSelect"
                            />
                            <input v-else-if="filter.widget == 'input'"
                                class="w-full h-full px-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-500"
                                v-model="filter.value"
                                :type="filter.type"
                                :placeholder="filter.placeholder"
                            />
                        </template>
                        <template v-slot:dragButton>
                            <div 
                                v-if="showDraggableFilters" 
                                draggable="true" 
                                @dragstart="handleDragStart($event, key)"
                                @dragend="handleDragEnd"
                                :data-filter-key="key"
                                class="flex h-8 px-1 items-center hover:bg-gray-200 hover:cursor-grab active:cursor-grabbing"
                                :class="{ 'is-dragging': key === draggedFilterKey}"
                            >
                                <VerticalDragSVG />
                            </div>
                        </template>
                    </FilterRow>
                </li>
                <li class="h-80" :key="-1"></li>
            </TransitionGroup>
            <div v-else class="flex w-full justify-center items-center text-center font-normal text-gray-700  pt-4">
                No Matching Filters
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, reactive, watch } from 'vue'
import QuickSearch from './QuickSearch.vue'
import CustomTabBar from './CustomTabBar.vue'
import FilterRow from './FilterRow.vue'
import ComboBox from './ComboBox.vue'
import ListBox from './ListBox.vue'
import PinFillSVG from '../svg-components/PinFillSVG.vue'
import PinOutlineSVG from '../svg-components/PinOutlineSVG.vue'
import VerticalDragSVG from '../svg-components/VerticalDragSVG.vue';
import InfiniteSpinnerSVG from '../svg-components/InfiniteSpinnerSVG.vue';

const props = defineProps(['filters', 'width', 'reorderFilters', 'defaultTab'])
const emit = defineEmits([
    'change-filters',
    'change-ordering',
    'change-active-filters',
    'set-filter-options',
    'hide-active-filters',
    'clear-select-all',
    'refetch-results',
    'toggle-pinned',
    'save-filter-order',
    'save-default-filters',
])

const quickSearchFilter = ref(null)
const isSavingDefaults = ref(false)

const filters = reactive(props.filters)

const draggedFilterKey = ref(null)

const filterTabs = reactive({
    all: {
        label: 'All Filters',
        isSelected: false,
        handleTabSelect: setFilterDisplayType.bind(null, 'all'),
        class: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-gray-600 text-xs',
        selectedClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-dark-primary underline text-xs',
        highlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-xs',
        unhighlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-xs',
    },
    active: {
        label: 'Active',
        isSelected: false,
        handleTabSelect: setFilterDisplayType.bind(null, 'active'),
        class: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-gray-600 text-xs',
        selectedClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-dark-primary underline text-xs',
        highlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-xs',
        unhighlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-xs',
    
    },
    pinned: {
        label: 'Pinned',
        isSelected: true,
        handleTabSelect: setFilterDisplayType.bind(null, 'pinned'),
        class: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-gray-600 text-xs',
        selectedClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-dark-primary underline text-xs',
        highlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-xs',
        unhighlightClass: 'whitespace-nowrap  tracking-wide px-0.5 h-full text-xs',
    },
})

const filterDisplayType = ref(props.defaultTab ? props.defaultTab : 'pinned')

const visibleFilters = reactive({})

const query = ref('')

const activeFilters = computed(() => {
    let activeFilterArr = []
    for(const filter in filters) {
        if(Array.isArray(filters[filter].value) && filters[filter].value.length > 0){
            activeFilterArr.push({filter: filter, name: filters[filter].label, value: filters[filter].options.filter(option => filters[filter].value.includes(option.value)).map(option => option.label)})
        } else if (!Array.isArray(filters[filter].value) && filters[filter].value && filters[filter].options) {
            activeFilterArr.push({filter: filter, name: filters[filter].label, value: filters[filter].options.filter(option => filters[filter].value == (option.value)).map(option => option.label)[0]})
        } else if (!Array.isArray(filters[filter].value) && filters[filter].value) {
            activeFilterArr.push({filter: filter, name: filters[filter].label, value: filters[filter].value})
        } else {
            if(filters[filter].min && filters[filter].min.value) {
                activeFilterArr.push({filter: `${filter}.min`, name: filters[filter].min.label, value: filters[filter].min.value})
            }
            if(filters[filter].max && filters[filter].max.value) {
                activeFilterArr.push({filter: `${filter}.max`, name: filters[filter].max.label, value: filters[filter].max.value})
            }
        }
    }
    return activeFilterArr
})

const activeFiltersString = computed(() => {
    return activeFilters.value ? activeFilters.value.map(filter => filter.value).toString() : ''
})

const filterValues = computed(() => {
    let values = []
    for(const filter in filters) {
        if(filters[filter].min || filters[filter].max) {
            if(filters[filter].min) {
                values.push(filters[filter].min.value)
            }
            if(filters[filter].max) {
                values.push(filters[filter].max.value)
            }
        } else {
        values.push(filters[filter].value)
        }
    }
    return values
})

const showDraggableFilters = computed(() => {
    return filterDisplayType.value == 'pinned';
})

const filterValuesString = computed(() => {
    return filterValues.value ? filterValues.value.toString() : '';
})

watch(filterValuesString, () => {
    emitFiltersChanged()
    emitClearSelectAll()
})

watch(activeFiltersString, () => {
    emitActiveFiltersChanged()
})

watch(() => quickSearchFilter.value, () => {
    setVisibleFilters()
})

watch(() => props.reorderFilters, () => {
    setVisibleFilters(0)
})

function setFilterTab(tabKey) {
    clearAllTabs()
    filterTabs[tabKey].isSelected = true;
    setFilterDisplayType(tabKey)
}

setFilterTab(filterDisplayType.value)
setVisibleFilters()

function clearAllTabs() {
    for(const tab of Object.values(filterTabs)) {
        tab.isSelected = false
    }
}


// ===========
function handleDragStart(event, key) {
    draggedFilterKey.value = key;
    event.dataTransfer.setData('text/plain', key);
}

function handleDragOver(event) {
    event.preventDefault()
    if (!draggedFilterKey.value) return;
    const targetRow = event.target.closest('[data-filter-key]')
    if (!targetRow) return;
    const targetKey = targetRow.dataset.filterKey;
    if(draggedFilterKey.value === targetKey) return;
    
    reorderFilterOrdinals(draggedFilterKey.value, targetKey)
}

function handleDragEnd () {
    draggedFilterKey.value = null;
    emitSaveFilterOrder()
    setVisibleFilters()
    // draggingIndex.value = -1;
}

function reorderFilterOrdinals(draggedKey, targetKey) {
    const draggedFilter = filters[draggedKey]
    const targetFilter = filters[targetKey]

    if(!draggedFilter || !targetFilter) return;

    const originalOrdinal = draggedFilter.ordinal;
    const targetOrdinal = targetFilter.ordinal;

    if (originalOrdinal === targetOrdinal) return;

    for (const key in filters) {
        const filter = filters[key]
        if(key === draggedKey) {
            filter.ordinal = targetOrdinal;
        } else if(originalOrdinal < targetOrdinal) {
            if(filter.ordinal > originalOrdinal && filter.ordinal <= targetOrdinal) {
                filter.ordinal--;
            }
        } else {
            if(filter.ordinal < originalOrdinal && filter.ordinal >= targetOrdinal) {
                filter.ordinal++;
            }
        }
    }
}

function emitSaveFilterOrder() {
    emit('save-filter-order')
}

// ===========

function emitFiltersChanged(){
    // console.log('emit: change-filters')
    emit("change-filters", filters)
}

function emitClearSelectAll(){
    emit('clear-select-all')
}

function emitActiveFiltersChanged(){
    // console.log('activeFiltersChanged')
    emit("change-active-filters", activeFilters.value)
}

function setFilterOptions(filterName, filterOptions) {
    // console.log('setFilterOptions', filterName, filterOptions)
    if(filterOptions) {
        // console.log("setFilterOptions emit: set-filter-options")
        emit('set-filter-options', filterName)
    }
}

function setFilterExpression(filter, expressionOption) {
    filter.expression.value = expressionOption
    emitFiltersChanged()
    emitClearSelectAll()
    emit('refetch-results')
}

function setFilter(filterName, target) {
    // console.log('setting filter', target)
    if(target.includes("clear")){
        filters[filterName].value = []
    }
}

function setFilterDefaults() {
    for(const filter in filters){
        filters[filter].value = filters[filter].defaultValue
    }
    if(activeFiltersString.value && activeFiltersString.value != '') {
        emitActiveFiltersChanged()
    }
}

function togglePinnedFilter(key) {
    emit('toggle-pinned', key)
}

async function saveDefaultFilters() {
    isSavingDefaults.value = true
    await new Promise((resolve) => {
        emit('save-default-filters', resolve)
    })
    isSavingDefaults.value = false
}


function setFilterDisplayType(displayType) {
    filterDisplayType.value = displayType
}

watch(() => filterDisplayType.value, () => {
    setVisibleFilters()
})

function isActive(filter) {
    if(Array.isArray(filter.value) && filter.value.length > 0){
        return true
    } else if(!Array.isArray(filter.value) && filter.value){
        return true
    } else if(filter.min && filter.min.value) {
        return true
    } else if(filter.max && filter.max.value) {
        return true
    }
    return false
}

function clearVisibleFilters() {
    // console.log('clearVisibleFilters')
    for (let visibleFilter in visibleFilters) delete visibleFilters[visibleFilter];
}

function setVisibleFilters() {
    clearVisibleFilters()
    // console.log('setVisibleFilters', quickSearchFilter.value)
    if(filterDisplayType.value=='all') {
        if(quickSearchFilter.value && quickSearchFilter.value != '') {
            Object.entries(filters).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([filterKey, filterValue]) => {
                // console.log(filterKey, filterValue)
                if(filterValue.visible) {
                    if(filterKey.toLowerCase().includes(quickSearchFilter.value) || filterValue.name.toLowerCase().includes(quickSearchFilter.value) || filterValue.label.toLowerCase().includes(quickSearchFilter.value)
                    ) {
                        visibleFilters[filterKey] = filterValue
                    } else {
                        if(!filterValue.pinned) {
                            delete visibleFilters[filterKey]
                        }
                    }
                } else {
                    delete visibleFilters[filterKey]
                }
            })
        } else {
            Object.entries(filters).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([filterKey, filterValue]) => {
                if(filterValue.visible) {
                    visibleFilters[filterKey] = filterValue
                } else {
                    if(!filterValue.pinned) {
                        delete visibleFilters[filterKey]
                    }
                }
            })
        }
    } else if (filterDisplayType.value == 'active') {
        if(quickSearchFilter.value && quickSearchFilter.value != '') {
            Object.entries(filters).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([filterKey, filterValue]) => {
                // console.log(filterKey, filterValue)
                if(filterValue.visible && isActive(filterValue)) {
                    if(filterKey.toLowerCase().includes(quickSearchFilter.value) || filterValue.name.toLowerCase().includes(quickSearchFilter.value) || filterValue.label.toLowerCase().includes(quickSearchFilter.value)
                    ) {
                        visibleFilters[filterKey] = filterValue
                    }
                } else {
                    delete visibleFilters[filterKey]
                }
            })
        } else {
            Object.entries(filters).sort(([, a], [, b]) => { return a.label.localeCompare(b.label)}).forEach(([filterKey, filterValue]) => {
                if(filterValue.visible && isActive(filterValue)) {
                    visibleFilters[filterKey] = filterValue
                }
            })
        }
    } else if (filterDisplayType.value == 'pinned') {
        if(quickSearchFilter.value && quickSearchFilter.value != '') {
            Object.entries(filters).filter(([, filter]) => {return filter.visible && filter.pinned}).sort(([, a], [, b]) => { return a.ordinal - b.ordinal}).forEach(([filterKey, filterValue]) => {
                // console.log(filterKey, filterValue)
                if(filterValue.pinned) {
                    if(filterKey.toLowerCase().includes(quickSearchFilter.value) || filterValue.name.toLowerCase().includes(quickSearchFilter.value) || filterValue.label.toLowerCase().includes(quickSearchFilter.value)
                    ) {
                        visibleFilters[filterKey] = filterValue
                    }
                } else {
                    delete visibleFilters[filterKey]
                }
            })
        } else {
            Object.entries(filters).filter(([, filter]) => {return filter.visible && filter.pinned}).sort(([, a], [, b]) => { return a.ordinal - b.ordinal}).forEach(([filterKey, filterValue]) => {
                if(filterValue.pinned) {
                    visibleFilters[filterKey] = filterValue
                }
            })
        }
    }
}

// setVisibleFilters()

function quickSearch(filterValue) {
    // console.log('quickSearch', filterValue)
    if(filterValue) {
        quickSearchFilter.value = filterValue.toLowerCase()
    } else {
        quickSearchFilter.value = null
    }
}

function clearQuickSearch() {
    // console.log('clearQuickSearch')
    quickSearchFilter.value = null
}

setFilterDefaults()
// setVisibleFilters()
</script>

<style scoped>
.filters-move,
.filters-enter-active {
    transition: all 0.3s ease;
}
.filters-leave-active {
  transition: all 0.3s ease;
}
.filters-enter-from,
.filters-leave-to {
  opacity: 0;
  /* transform: translateX(-30px); */
}

.filters-leave-active {
    position: absolute;
    /* position: relative; */
}
</style>